import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import styles from "./SelectItemWithDescription.module.css";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import ErrorAddOn from "../TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";

export type ItemWithDescription = {
  label: string;
  value: string;
  requireDescription: boolean;
};

type SelectItemWithDescriptionProps = {
  items: ItemWithDescription[];
  value: string;
  label: string;
  description: string;
  onConfirmClick: (newItem: {
    value: string;
    label: string;
    description: string;
  }) => void;
  disabled: boolean;
  title: string;
  isError?: boolean;
};

function SelectItemWithDescription({
  items,
  value,
  label,
  description,
  onConfirmClick,
  disabled,
  title,
  isError = false,
}: SelectItemWithDescriptionProps) {
  const [showModal, setShowModal] = useState(false);
  const [newValue, setNewValue] = useState<string>(value);
  const [newLabel, setNewLabel] = useState<string>(label);
  const [newDescription, setNewDescription] = useState<string>(description);
  const [shouldValidate, setShouldValidate] = useState(false);

  const { t } = useTranslation();

  const selectedItem = useMemo(
    () => items.find((item) => item.value === newValue),
    [items, newValue]
  );

  const requiredDescription = selectedItem?.requireDescription ?? false;
  const validDescription =
    !requiredDescription || newDescription.trim().length > 0;

  const hasError = !validDescription;

  const handleOpenModal = useCallback(() => {
    setNewValue(value);
    setNewLabel(label);
    setNewDescription(description);
    setShowModal(true);
    setShouldValidate(false);
  }, [value, label, description]);

  const {
    saveGuardedFunction: handleCloseModal,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
      setShouldValidate(false);
    }, [])
  );

  const handleConfirm = useCallback(() => {
    if (hasError) {
      setShouldValidate(true);
      return;
    }

    onConfirmClick({
      value: newValue,
      label: newLabel,
      description: newDescription,
    });
    setShowModal(false);
    setShouldValidate(false);
    disableSaveGuard();
  }, [
    hasError,
    onConfirmClick,
    newValue,
    newLabel,
    newDescription,
    disableSaveGuard,
  ]);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNewDescription(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleItemChange = useCallback(
    (nextValue: string) => {
      setNewValue(nextValue);
      setNewLabel(items.find((item) => item.value === nextValue)?.label || "");
      setNewDescription("");
      enableSaveGuard();
    },
    [enableSaveGuard, items]
  );

  return (
    <>
      <FixaListViewItem
        title={title}
        inset
        onClick={handleOpenModal}
        emphasised={!!label}
        description={t(label)}
        control="navigational"
        controlIcon={null}
        disabled={disabled}
        addOn={
          isError ? <ErrorAddOn>{t("pleaseFillInString")}</ErrorAddOn> : null
        }
      />
      <FixaModal
        visible={showModal}
        handleCloseBtn={handleCloseModal}
        data-testid="SelectItemWithDescription-FixaModal"
      >
        <FixaSheets
          labelledById="task-management-select-item-with-description-header"
          header={
            <FixaModalHeader
              id="task-management-select-item-with-description-header"
              title={title}
              closeBtnClick={handleCloseModal}
            />
          }
          footer={
            <FixaModalFooter renderBorder>
              <FixaButton
                type="primary"
                fluid
                text={t("confirmString")}
                onClick={handleConfirm}
              />
            </FixaModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <FixaListView id="SelectItemWithDescription-ListView">
              {items.map((item) => {
                const isSelected = newValue === item.value;

                return (
                  <div key={item.value}>
                    <FixaListViewItem
                      id={item.value}
                      title={t(item.label)}
                      onChange={() => handleItemChange(item.value)}
                      control="radiobutton"
                      controlProps={{
                        checked: isSelected,
                        value: item.value,
                      }}
                      disabled={disabled}
                    />

                    {isSelected && (
                      <FixaFormField
                        className={styles["formField"]}
                        valid={validDescription}
                        validation={{
                          type: "error",
                          msg: t("pleaseFillInString"),
                          id: "descriptionValidation",
                        }}
                        shouldValidate={
                          shouldValidate && item.requireDescription
                        }
                      >
                        <FixaTextArea
                          label={t("description4String")}
                          value={newDescription}
                          onChange={handleDescriptionChange}
                        />
                      </FixaFormField>
                    )}
                  </div>
                );
              })}
            </FixaListView>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}

export default SelectItemWithDescription;
